import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { LoaderCircleIcon, Loader2, Upload, ChevronDown, Check, Rat, Download, TriangleAlert, Info, CircleMinus, Send, MoreHorizontal, CirclePlus } from "lucide-react"
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { TranscriberPayment, useTranscribersPayments } from "@/contexts/TranscribersPaymentsContext";
import { cn, debounce } from "@/lib/utils.ts";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert.tsx";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip.tsx";
import {
    Combobox,
    ComboboxAnchor,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxItem,
    ComboboxTrigger,
} from "@/components/ui/combobox-diceui";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import * as FileUpload from "@/components/ui/file-upload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from '@/components/ui/dropdown-menu';
import { useToast } from "@/hooks/use-toast"

import PaymentStatusBadge from "./components/PaymentStatusBadge.tsx";

const SERVICE_LABEL = {
    layout: "Mise en page",
    transcription: "Transcription",
    translation: "Traduction",
    correction: "Correction",
    transcription_with_correction: "Transcription (Corrigée)",
}

export default function PaymentDetails({ transcriber_payment_id }: { transcriber_payment_id: number }) {
    const navigate = useNavigate();
    const addressInputRefs = {
        company_name: React.useRef(null),
        street: React.useRef(null),
        city: React.useRef(null),
        state: React.useRef(null),
        postal_code: React.useRef(null),
    };
    const {
        state: {
            fetchTranscribersPayments: { data },
            toggleTranscriberPaymentTaxes: { loading: loadingTaxes },
            toggleTranscriberPaymentType: { loading: loadingPaymentType },
            getProccessingTranscriberFolders: { data: processing_folders },
        },
        actions
    } = useTranscribersPayments();
    const { user: { transcriber } } = useAuth();
    const [isAddExtraInvoiceExtraDialogOpen, setIsAddExtraInvoiceExtraDialogOpen] = useState(false);

    const [transcriber_payment, setTranscriberPayment] = useState();
    const updateTranscriberTaxesPaymentSettings = useCallback(
        debounce(actions.updateTranscriberTaxesPaymentSettings, 2000),
        [actions.updateTranscriberTaxesPaymentSettings]
    );
    const updateTranscriberPaymentInvoiceRef = useCallback(
        debounce(actions.updateTranscriberPaymentInvoiceRef, 777),
        [actions.updateTranscriberPaymentInvoiceRef]
    );
    const updateTranscriberAddressPaymentSettings = useCallback(
        debounce(() => {
            const address = {
                company_name: addressInputRefs.company_name.current?.value,
                street: addressInputRefs.street.current?.value,
                city: addressInputRefs.city.current?.value,
                state: addressInputRefs.state.current?.value,
                postal_code: addressInputRefs.postal_code.current?.value,
            };
            console.log(address);
            actions.updateTranscriberAddressPaymentSettings(address);
        }, 2000),
        [actions.updateTranscriberAddressPaymentSettings, addressInputRefs]
    );

    useEffect(() => {
        const payment = data.find(payment => payment.id.toString() === transcriber_payment_id);

        if (payment) {
            setTranscriberPayment(payment);
        } else {
            navigate("/payments");
        }

    }, [data, transcriber_payment_id])

    useEffect(() => {
        actions.getProccessingTranscriberFolders();
    }, []);

    if (!transcriber_payment) return (
        <p className="min-h-[90vh] text-sm text-muted-foreground flex items-center justify-center text-lg select-none">
            <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
            Chargement
        </p>
    );

    const folderRows = transcriber_payment.invoice.rows.filter(row => row.folder);
    const extraLineRows = transcriber_payment.invoice.rows.filter(row => !row.folder && row.extra);

    const optional_columns = [
        folderRows.some(row => row.folder.pages_rate) && 'pages',
        folderRows.some(row => row.folder.minutes_rate) && 'minutes',
    ].filter(Boolean);

    const has_multiple_services = folderRows.some(row => row.folder.service !== folderRows[0].folder.service);

    return (
        <div className="w-full p-4">
            <div className="flex justify-between">
                <div className="flex gap-x-2 items-center mb-4">
                    <h1 className="text-2xl font-bold">#{transcriber_payment.invoice.ref}</h1>
                    <PaymentStatusBadge status={transcriber_payment.status} />
                </div>
                {/* dowload button transcriber_payment.invoice.path exist */}
                {transcriber_payment.paid_at && (
                    <Button variant="outline" className="mb-4 flex items-center gap-x-2" onClick={() => actions.downloadTranscriberPaymentInvoice(transcriber_payment)}>
                        <Download className="h-4 w-4" />
                        Télécharger la facture
                    </Button>
                )}
            </div>

            {transcriber_payment.declined_at && transcriber_payment.declined_reason && (
                <div className="bg-destructive/10 p-3 rounded-md mb-4">
                    <p className="text-sm font-medium text-destructive">Raison du refus: {transcriber_payment.declined_reason}</p>
                </div>
            )}

            {transcriber_payment.status !== 'draft' && (
                <Card className="mb-6">
                    <CardContent className="pt-4 pb-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-muted-foreground">Demandé le</p>
                                <p>{format(new Date(transcriber_payment.asked_at), 'PPP', { locale: fr })}</p>
                            </div>
                            {transcriber_payment.paid_at && (
                                <div>
                                    <p className="text-muted-foreground">Payé le</p>
                                    <p>{format(new Date(transcriber_payment.paid_at), 'PPP', { locale: fr })}</p>
                                </div>
                            )}
                            {transcriber_payment.canceled_at && (
                                <div>
                                    <p className="text-muted-foreground">Annulé le</p>
                                    <p>{format(new Date(transcriber_payment.canceled_at), 'PPP', { locale: fr })}</p>
                                </div>
                            )}
                            {transcriber_payment.declined_at && (
                                <div>
                                    <p className="text-muted-foreground">Refusé le</p>
                                    <p>{format(new Date(transcriber_payment.declined_at), 'PPP', { locale: fr })}</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {transcriber_payment.status === 'draft' && (
                <Tabs value={transcriber_payment?.invoice?.is_custom ? 'manual' : 'automatic'}>
                    <TabsList className="w-full mb-4">
                        <TabsTrigger
                            disabled={loadingPaymentType}
                            value="automatic"
                            className="flex-1"
                            onClick={() => actions.toggleTranscriberPaymentType(transcriber_payment)}
                        >
                            Automatique
                        </TabsTrigger>
                        <TabsTrigger
                            disabled={loadingPaymentType}
                            value="manual"
                            className="flex-1"
                            onClick={() => actions.toggleTranscriberPaymentType(transcriber_payment)}
                        >
                            Manuel
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            )}

            {(transcriber_payment.invoice.is_custom || transcriber_payment.invoice.rows.length) ? (
                <>
                    {transcriber_payment.status === 'draft' && transcriber_payment.invoice.is_custom && <PaymentInvoiceDropBox transcriber_payment={transcriber_payment} />}

                    <div className={cn("mb-3", (transcriber_payment.status !== 'draft' || !transcriber_payment.invoice.is_custom) && 'mt-4')}>
                        <Label htmlFor="invoice-ref-auto">Informations de facturation</Label>
                        <div className="flex gap-x-2 mb-2">
                            <Input
                                id="invoice-ref-auto"
                                disabled={transcriber_payment.status !== 'draft'}
                                defaultValue={transcriber_payment.invoice.ref}
                                onChange={(e) => updateTranscriberPaymentInvoiceRef(transcriber_payment, e.target.value)}
                                placeholder="Référence de la facture"
                            />
                            {!transcriber_payment.invoice.is_custom && (
                                <Input
                                    ref={addressInputRefs.company_name}
                                    id="company-name-input"
                                    name="company-name"
                                    placeholder="Nom de l'entreprise"
                                    disabled={transcriber_payment.status !== 'draft'}
                                    defaultValue={transcriber?.settings?.payment?.address?.company_name}
                                    onChange={updateTranscriberAddressPaymentSettings}
                                />
                            )}
                        </div>
                        {!transcriber_payment.invoice.is_custom && (
                            <>
                                <div className="flex gap-x-2 mb-2">
                                    <Input
                                        ref={addressInputRefs.street}
                                        id="street-input"
                                        name="street"
                                        placeholder="Rue"
                                        disabled={transcriber_payment.status !== 'draft'}
                                        defaultValue={transcriber?.settings?.payment?.address?.street}
                                        onChange={updateTranscriberAddressPaymentSettings}
                                    />
                                    <Input
                                        ref={addressInputRefs.city}
                                        id="city-input"
                                        name="city"
                                        placeholder="Ville"
                                        disabled={transcriber_payment.status !== 'draft'}
                                        defaultValue={transcriber?.settings?.payment?.address?.city}
                                        onChange={updateTranscriberAddressPaymentSettings}
                                    />
                                </div>
                                <div className="flex gap-x-2 mb-2">
                                    <Input
                                        ref={addressInputRefs.state}
                                        id="state-input"
                                        name="state"
                                        placeholder="Etat"
                                        disabled={transcriber_payment.status !== 'draft'}
                                        defaultValue={transcriber?.settings?.payment?.address?.state}
                                        onChange={updateTranscriberAddressPaymentSettings}
                                    />
                                    <Input
                                        ref={addressInputRefs.postal_code}
                                        id="postal-code-input"
                                        name="postal_code"
                                        placeholder="Code postal"
                                        disabled={transcriber_payment.status !== 'draft'}
                                        defaultValue={transcriber?.settings?.payment?.address?.postal_code}
                                        onChange={updateTranscriberAddressPaymentSettings}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {transcriber_payment.status === 'draft' && (
                        <div className="mb-3 flex items-center gap-x-2">
                            <Checkbox id="enabled-taxes" disabled={loadingTaxes} checked={Boolean(transcriber_payment.invoice.taxes)} onClick={() => actions.toggleTranscriberPaymentTaxes(transcriber_payment)} />
                            <Label htmlFor="enabled-taxes">Activer les taxes</Label>
                        </div>
                    )}

                    {transcriber_payment.invoice.taxes && (
                        <div className="flex gap-x-2 mb-6">
                            <div className="w-full">
                                <Label htmlFor="tps-input">Numéro TPS</Label>
                                <Input
                                    id="tps-input"
                                    name="tps"
                                    disabled={transcriber_payment.status !== 'draft'}
                                    defaultValue={transcriber?.settings?.payment?.taxes?.tps}
                                    onChange={(ev) => updateTranscriberTaxesPaymentSettings({ tps: ev.target.value })}
                                />
                            </div>
                            <div className="w-full">
                                <Label htmlFor="tvq-input">Numéro TVQ</Label>
                                <Input
                                    id="tvq-input"
                                    name="tvq"
                                    disabled={transcriber_payment.status !== 'draft'}
                                    defaultValue={transcriber?.settings?.payment?.taxes?.tvq}
                                    onChange={(ev) => updateTranscriberTaxesPaymentSettings({ tvq: ev.target.value })}
                                />
                            </div>
                        </div>
                    )}

                    {transcriber_payment.status === 'draft' && !transcriber_payment.invoice.is_custom && processing_folders?.length > 0 && (
                        <Alert className="bg-blue-300/30 mb-4 flex flex-row items-center space-x-2">
                            <Info className="!top-1/2 !-translate-y-1/2" />
                            <div>
                                <AlertTitle>Dossiers en cours de complétion</AlertTitle>
                                <AlertDescription>
                                    <p>Les dossiers suivant sont en cours de complétion et ne seront pas inclus dans la facture:</p>
                                    <p>
                                        {processing_folders.map((folder) => (
                                            <span key={folder.id} className="text-sm font-medium text-muted-foreground">#{folder.pydio_id}</span>
                                        )).reduce((prev, curr) => [prev, ', ', curr])}
                                    </p>
                                </AlertDescription>
                            </div>
                        </Alert>
                    )}

                    {transcriber_payment.status === 'draft' && transcriber_payment.invoice.is_custom && (
                        <div className="mb-6">
                            <UnpaidFoldersSelect transcriber_payment={transcriber_payment} />
                        </div>
                    )}

                    {folderRows.length > 0 && (
                        <div className="relative">
                            <div className="flex">
                                {/* Floating menu column to the left */}
                                <div className="absolute -left-[25px] flex flex-col items-center pt-[40px] pr-2">
                                    {folderRows.map((row: any) => (
                                        <div className="h-[49px] flex items-center">
                                            <DropdownMenu key={row.folder.pydio_id}>
                                                <DropdownMenuTrigger asChild>
                                                    <button className="text-muted-foreground hover:text-white">
                                                        <MoreHorizontal size={16} />
                                                    </button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent side="left" align="start">
                                                    {!transcriber_payment.invoice.is_custom && (
                                                        <DropdownMenuItem onClick={() => actions.toggleTranscriberPaymentFolderServicePospone(transcriber_payment.id, { folder_id: row.folder.id, service: row.folder.service })}>
                                                            {row.pospone ? 'Inclure dans cette facture' : 'Reporter à la prochaine facture'}
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuItem onClick={() => actions.addFolderPaidService(transcriber_payment.id, { folder_id: row.folder.id, service: row.folder.service })}>
                                                        Marquer comme déjà payé
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    ))}
                                </div>

                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[60px]">#</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Docteur</TableHead>
                                            {has_multiple_services && <TableHead>Service</TableHead>}
                                            <TableHead>Durée</TableHead>
                                            {optional_columns.includes('minutes') && <TableHead>Taux minutes</TableHead>}
                                            {optional_columns.includes('pages') && <TableHead>Pages</TableHead>}
                                            {optional_columns.includes('pages') && <TableHead>Taux pages</TableHead>}
                                            {!transcriber_payment.invoice.is_custom && <TableHead>Extra</TableHead>}
                                            {!transcriber_payment.invoice.is_custom && <TableHead className="text-right">Total</TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {folderRows.map((row: any) => {
                                            const index = transcriber_payment.invoice.rows.findIndex((r: any) => r.folder.id === row.folder.id);
                                            return (
                                                <TableRow key={row.folder.pydio_id} className="even:bg-accent/50">
                                                    <TableCell>
                                                        {row.folder.pydio_id}
                                                    </TableCell>
                                                    <TableCell>{format(new Date(row.folder.date), 'yyyy-MM-dd')}</TableCell>
                                                    <TableCell>{row.folder.doctor}</TableCell>
                                                    {has_multiple_services && <TableCell>{SERVICE_LABEL[row.folder.service]}</TableCell>}
                                                    <TableCell>{row.folder.minutes}m {row.folder.seconds}s</TableCell>
                                                    {optional_columns.includes('minutes') && <TableCell>
                                                    {row.folder.minutes_rate && row.folder.minutes_rate.toFixed(2)}
                                                    {row.folder.minutes_rate && Boolean(row.folder.minutes_rate_variation) && <sup>({row.folder.minutes_rate_variation > 0 && '+'}{row.folder.minutes_rate_variation.toFixed(2)})</sup>}
                                                    {row.folder.minutes_rate && row.folder.percentage_variation && row.folder.percentage_variation !== 1 && <sup>(+{((row.folder.percentage_variation*100)-100)}%)</sup>}
                                                    </TableCell>}
                                                    {optional_columns.includes('pages') && <TableCell>{row.folder.pages_rate ? row.folder.pages : ''}</TableCell>}
                                                    {optional_columns.includes('pages') && <TableCell>
                                                        {row.folder.pages_rate?.toFixed(2)}
                                                        {row.folder.pages_rate && row.folder.percentage_variation && row.folder.percentage_variation !== 1 && <sup>(+{((row.folder.percentage_variation*100)-100)}%)</sup>}
                                                    </TableCell>}
                                                    {!transcriber_payment.invoice.is_custom && (
                                                        <TableCell>
                                                            <InvoiceRowExtraDialog transcriber_payment={transcriber_payment} row_index={index} />
                                                        </TableCell>
                                                    )}
                                                    {!transcriber_payment.invoice.is_custom && (
                                                        <TableCell className="text-right">{row.pospone ? <span className="text-muted-foreground">(Reporter)</span> : `${row.amount.toFixed(2)} $`}</TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {!transcriber_payment.invoice.is_custom && (
                        <div className="mb-2">
                            <ExtraLinesTable transcriber_payment={transcriber_payment} extraLineRows={extraLineRows} setIsAddExtraInvoiceExtraDialogOpen={setIsAddExtraInvoiceExtraDialogOpen} />
                        </div>
                    )}

                    {(transcriber_payment.invoice.rows.length > 0 || transcriber_payment.invoice.is_custom) && (
                        <div className="mb-6">
                            <div className="bg-accent/50 p-3 rounded-md">
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    {transcriber_payment.invoice.taxes && (
                                        <>
                                            <div>Total avant taxes:</div>
                                            <div className="text-right flex justify-end">
                                                {!transcriber_payment.invoice.is_custom || transcriber_payment.status !== 'draft'
                                                    ? (
                                                        <div>
                                                            {transcriber_payment.invoice.total.before_taxes_amount.toFixed(2)} $
                                                        </div>  
                                                    )
                                                    : (
                                                        <div className="flex gap-x-1 items-center">
                                                            <CustomTranscriberPaymentTotalInput transcriber_payment={transcriber_payment} property="before_taxes_amount" />
                                                            <div>$</div>
                                                        </div>
                                                    )}
                                            </div>
                                            <div>TVQ:</div>
                                            <div className="text-right">{transcriber_payment.invoice.taxes.tvq.toFixed(2)} $</div>
                                            <div>TPS:</div>
                                            <div className="text-right">{transcriber_payment.invoice.taxes.tps.toFixed(2)} $</div>
                                            <div>Total taxes:</div>
                                            <div className="text-right">{transcriber_payment.invoice.taxes.amount.toFixed(2)} $</div>
                                        </>
                                    )}

                                    <Separator className="col-span-2 my-1" />

                                    <div className="font-medium">Total:</div>
                                    <div className="text-right font-medium flex justify-end">
                                        {(transcriber_payment.invoice.is_custom && transcriber_payment.invoice.taxes) || !transcriber_payment.invoice.is_custom || transcriber_payment.status !== 'draft' ? `${transcriber_payment.invoice.total.amount.toFixed(2)} $` : (
                                            <div className="flex gap-x-1 items-center">
                                                <CustomTranscriberPaymentTotalInput transcriber_payment={transcriber_payment} property="amount" />
                                                <div>$</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex  mb-2">
                        <PaymentDetailsActions transcriber_payment={transcriber_payment} setIsAddExtraInvoiceExtraDialogOpen={setIsAddExtraInvoiceExtraDialogOpen} />
                    </div>
                </>
            ) : (
                <div className="flex flex-col items-center gap-y-4 justify-center h-[20vh]">
                    <Rat className="w-20 h-20" strokeWidth={1} />
                    <p className="text-md text-muted-foreground">Aucuns dossiers impayés.</p>
                </div>
            )}
            <AddExtraInvoiceExtraDialog
                open={isAddExtraInvoiceExtraDialogOpen}
                onOpenChange={setIsAddExtraInvoiceExtraDialogOpen}
                transcriber_payment={transcriber_payment}
            />
        </div>
    )
}

function CustomTranscriberPaymentTotalInput({ transcriber_payment, property }: { transcriber_payment: TranscriberPayment, property: 'amount' | 'before_taxes_amount' }) {
    const { actions } = useTranscribersPayments();
    const { toast } = useToast();

    const onChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.value === "") return actions.setCustomTranscriberPaymentTotal(transcriber_payment, 0);

        const total = parseFloat(ev.target.value.replace(',', '.'));

        if (isNaN(total)) {
            return toast({
                title: "Montant total incorrect",
                description: "Le montant total doit être un nombre. (ex: 100.00)",
                variant: "destructive",
                duration: 2000
            });
        }

        await actions.setCustomTranscriberPaymentTotal(transcriber_payment, total);

        if (property === 'amount') {
            toast({
                title: "Montant total modifié",
                description: "Le montant total de la facture a été modifié.",
                variant: "default",
                duration: 2000
            })
        }
    }

    const debounceOnChange = useCallback(debounce(onChange, 1000), [onChange]);
    const value = transcriber_payment.invoice.total[property];

    return (
        <Input
            className={cn("max-w-[60px] h-6.5 money-input text-right p-0 border-orange-300", value && '!border-green-300')}
            defaultValue={value.toFixed(2)}
            onChange={debounceOnChange}
        />
    )
}

function InvoiceRowExtraDialog({ transcriber_payment, row_index }: { transcriber_payment: TranscriberPayment, row_index?: number }) {
    const amountRef = React.useRef(null);
    const reasonRef = React.useRef(null);
    const closeButtonRef = React.useRef(null);

    const {
        actions,
        state: {
            setTranscriberPaymentInvoiceRowExtra: { loading },
            setTranscriberPaymentInvoiceExtra: { loading: loading2 },
        },
    } = useTranscribersPayments();

    const has_row_index = row_index !== undefined;
    const current = has_row_index ? transcriber_payment.invoice.rows[row_index] : transcriber_payment.invoice;
    const extra = { amount: 0, reason: '', ...current.extra };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const amount = parseFloat(amountRef.current?.value || "0") || 0;
        const reason = reasonRef.current?.value || "";
        const action = has_row_index ? 'setTranscriberPaymentInvoiceRowExtra' : 'setTranscriberPaymentInvoiceExtra';

        actions[action]({
            transcriber_payment_id: transcriber_payment.id,
            transcriber_payment_invoice_row_index: row_index,
            extra: { amount, reason }
        }).then(() => closeButtonRef.current?.click())
    }

    const disabled = transcriber_payment.status !== 'draft';

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button 
                    variant="outline" 
                    className="!py-1 !px-2 !h-8" 
                    disabled={disabled || !has_row_index} 
                >
                    {(extra?.amount || 0).toFixed(2)} $
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Configuration d'un extra</DialogTitle>
                    <DialogDescription>
                        {has_row_index ? (
                            <>Ajouter un montant supplémentaire à la facture pour ce dossier.</>
                        ) : (
                            <>Ajouter un montant supplémentaire à la facture.</>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="extra-amount" className="text-right">Montant</Label>
                        <Input ref={amountRef} id="extra-amount" type="number" defaultValue={extra?.amount || 0} className="col-span-3" required />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="extra-reason" className="text-right">Raison</Label>
                        <Input ref={reasonRef} id="extra-reason" defaultValue={extra?.reason || ''} className="col-span-3" required />
                    </div>
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button ref={closeButtonRef} variant="outline">Annuler</Button>
                    </DialogClose>
                    <Button type="submit" form="invoice-row-extra-form" disabled={loading || loading2} onClick={onSubmit}>
                        Enregistrer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function AddExtraInvoiceExtraDialog({ open, onOpenChange, transcriber_payment }: { open: boolean, onOpenChange: (open: boolean) => void, transcriber_payment: TranscriberPayment }) {
    const reasonRef = React.useRef(null);
    const amountRef = React.useRef(null);
    const {
        actions,
        state: { addTranscriberPaymentInvoiceExtraRow: { loading: addingExtraRow } }
    } = useTranscribersPayments(); // Assuming actions are available from context
    const { toast } = useToast();
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const reason = reasonRef.current?.value;
        const amountString = amountRef.current?.value;
        const amount = parseFloat(amountString);

        if (reason && reason.trim() !== "" && !isNaN(amount) && amount !== 0) { // amount can be negative for deductions
            try {
                // CONCEPTUAL ACTION: You'll need to implement this in your context
                // This action should add the new extra line to transcriber_payment.invoice.rows
                // and trigger backend sync / total recalculation.
                await actions.addTranscriberPaymentInvoiceExtraRow(transcriber_payment.id, { reason, amount });
                toast({
                    title: "Ligne supplémentaire ajoutée",
                    description: "La ligne a été ajoutée à la facture.",
                    variant: "success",
                });
                onOpenChange(false); // Close dialog
            } catch (error) {
                console.error("Failed to add extra line:", error);
                toast({
                    title: "Erreur",
                    description: "Impossible d'ajouter la ligne supplémentaire.",
                    variant: "destructive",
                });
            }
        } else {
            toast({
                title: "Données invalides",
                description: "Veuillez entrer une description valide et un montant non nul.",
                variant: "destructive",
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Ajouter une ligne supplémentaire</DialogTitle>
                    <DialogDescription>
                        Saisissez la description et le montant pour la ligne supplémentaire.
                    </DialogDescription>
                </DialogHeader>
                <form id="add-extra-line-form" onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="extra-line-reason" className="text-right">Description</Label>
                            <Input ref={reasonRef} id="extra-line-reason" className="col-span-3" required />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="extra-line-amount" className="text-right">Montant</Label>
                            <Input ref={amountRef} id="extra-line-amount" type="number" step="0.01" className="col-span-3" required />
                        </div>
                    </div>
                </form>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button type="submit" form="add-extra-line-form" disabled={addingExtraRow}>
                        Ajouter
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
    
function ExtraLinesTable({ transcriber_payment, extraLineRows, setIsAddExtraInvoiceExtraDialogOpen }) {
    const {
        actions,
        state: { deleteTranscriberPaymentInvoiceExtraRow: { loading: deletingExtraRow } },
    } = useTranscribersPayments(); // Assuming actions are available from context
    const { toast } = useToast();

    const handleDeleteExtraLine = async (rowToDelete) => {
        const originalIndex = transcriber_payment.invoice.rows.indexOf(rowToDelete);
        if (originalIndex !== -1) {
            try {
                await actions.deleteTranscriberPaymentInvoiceExtraRow(transcriber_payment.id, originalIndex);
                toast({
                    title: "Ligne supplémentaire supprimée",
                    variant: "success",
                });
            } catch (error) {
                console.error("Failed to delete extra line:", error);
                toast({
                    title: "Erreur",
                    description: "Impossible de supprimer la ligne supplémentaire.",
                    variant: "destructive",
                });
            }
        } else {
            toast({ title: "Erreur interne", description: "Ligne supplémentaire non trouvée pour suppression.", variant: "destructive" });
        }
    };

    return (
        <div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="flex items-center justify-between">
                            <div className="flex items-center gap-x-2">
                                <div>Autres</div>
                                {transcriber_payment.status === 'draft' && <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="px-2 text-primary"
                                            onClick={() => setIsAddExtraInvoiceExtraDialogOpen(true)}
                                        >
                                            <CirclePlus />
                                        </Button>
                                    </TooltipTrigger>

                                    <TooltipContent side="right">
                                        <p>Ajouter une ligne supplémentaire</p>
                                    </TooltipContent>
                                </Tooltip>}
                            </div>
                            {!extraLineRows.length && <div className="text-right">0.00 $</div>}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {extraLineRows.map((row, index) => (
                        <TableRow key={`extra-line-${index}`} className="even:bg-accent/50">
                            <TableCell className="flex justify-between items-center">
                                <div className="flex items-center gap-x-2">
                                    {transcriber_payment.status === 'draft' && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-red-500 hover:text-red-700 px-2"
                                                    onClick={() => handleDeleteExtraLine(row)}
                                                    disabled={deletingExtraRow}
                                                >
                                                    <CircleMinus />
                                                </Button>
                                            </TooltipTrigger>

                                            <TooltipContent side="left">
                                                <p>Supprimer la ligne</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                    <div>{row.extra.reason}</div>
                                </div>
                                <div className="text-right">{row.extra.amount.toFixed(2)} $</div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function PaymentDetailsActions({ transcriber_payment, setIsAddExtraInvoiceExtraDialogOpen }: { transcriber_payment: TranscriberPayment, setIsAddExtraInvoiceExtraDialogOpen: (open: boolean) => void }) {
    const { actions, state } = useTranscribersPayments();
    const { user: { transcriber } } = useAuth();
    const { toast } = useToast();

    const asked_transcriber_payment = state.fetchTranscribersPayments.data.find((payment) => payment.status === 'asked');

    const onCancelAskedTranscriberPaymentClick = () => (
        actions.cancelTranscriberPayment(asked_transcriber_payment)
            .then(() => actions[transcriber_payment.invoice.is_custom ? 'getUnpaidFolders' : 'syncTranscriberPayment'](transcriber_payment))
    )

    const onAskTranscriberPaymentClick = () => (
        actions.askTranscriberPayment(transcriber_payment)
            .then(() => (
                toast({
                    title: "Demande de paiement envoyée",
                    description: "Votre demande de paiement a été envoyée avec succès.",
                    variant: "success",
                    duration: 2000
                })
            ))
    );

    const has_no_invoice_rows = !transcriber_payment.invoice.is_custom && transcriber_payment.invoice.rows.filter((row) => !row.pospone).length === 0;
    const is_custom_without_invoice = transcriber_payment.invoice.is_custom && !transcriber_payment.invoice.path;
    const is_custom_without_total = transcriber_payment.invoice.is_custom && transcriber_payment.invoice.total.amount === 0;
    const missing_transcriber_informations = !transcriber_payment.invoice.is_custom && Object.keys(transcriber?.settings?.payment?.address || {}).length < 4 || Object.values(transcriber?.settings?.payment?.address || {}).some((v) => !v.length);
    const missing_taxes_informations = transcriber_payment.invoice.taxes && Object.keys(transcriber?.settings?.payment?.taxes || {}).length < 2;

    const errors = {
        has_no_invoice_rows,
        is_custom_without_invoice,
        is_custom_without_total,
        missing_transcriber_informations,
        missing_taxes_informations
    };
    const has_errors = Object.values(errors).some((v) => v);

    return (
        <div className="w-full">
            {transcriber_payment.status === 'draft' && asked_transcriber_payment && (
                <Alert className="bg-orange-300/30 mb-4">
                    <div className="flex justify-between">
                        <div>
                            <AlertTitle>Un autre paiement est actuellement en cours (#{asked_transcriber_payment.invoice.ref})</AlertTitle>
                            <AlertDescription>
                                Annuler le paiement en cours pour faire une demande contenant les nouveaux dossiers.
                            </AlertDescription>
                        </div>
                        <Button disabled={state.cancelTranscriberPayment.loading} variant="link" className="text-sm" onClick={onCancelAskedTranscriberPaymentClick}>
                            {state.cancelTranscriberPayment.loading ? <Loader2 className="animate-spin" /> : "Annuler le paiement en cours"}
                        </Button>
                    </div>
                </Alert>
            )}
            <div>
                {has_errors && (
                    <Alert className="bg-orange-300/30 mb-4">
                        <TriangleAlert />
                        <AlertTitle>Attention</AlertTitle>
                        <AlertDescription>
                            <div>
                                {has_no_invoice_rows && <p>Impossible d'envoyer une demande pour facture sans dossiers.</p>}
                                {is_custom_without_invoice && <p>Une facture est necessaire.</p>}
                                {is_custom_without_total && <p>Le montant total de la facture doit être supérieur à 0.</p>}
                                {missing_transcriber_informations && <p>Veuillez remplir les informations de facturation</p>}
                                {missing_taxes_informations && <p>Veuillez remplir vos numéros TVQ/TPS</p>}
                            </div>
                        </AlertDescription>
                    </Alert>
                )}
            </div>
            <div className="flex gap-x-2 justify-end">
                {transcriber_payment.status === 'draft' && !asked_transcriber_payment && (
                    <Button
                        variant="default"
                        disabled={state.askTranscriberPayment.loading || has_errors}
                        onClick={onAskTranscriberPaymentClick}
                    >
                        <Send />
                        Envoyer
                    </Button>
                )}
                {transcriber_payment.status === 'asked' && (
                    <Button variant="default" onClick={() => actions.cancelTranscriberPayment(transcriber_payment)}>Annuler</Button>
                )}
            </div>
        </div>
    )
}

function displayDuration (duration: number) {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;

    // dont display if 0
    const display = [];
    if (hours > 0) display.push(`${hours} heures`);
    if (minutes > 0) display.push(`${minutes} minutes`);
    if (seconds > 0) display.push(`${seconds} secondes`);

    return display.join(" ");
}

export function UnpaidFoldersSelect({ transcriber_payment }: { transcriber_payment: TranscriberPayment }) {
    const { state: { getUnpaidFolders: { data: unpaid_folders } }, actions } = useTranscribersPayments();

    useEffect(() => {
        actions.getUnpaidFolders();
    }, []);

    const value = transcriber_payment.invoice.rows.map((row) => `${row.folder.id}:${row.folder.service}`);

    const onValueChange = (value) => {
        const folders_services = value.map((v) => {
            const [folder_id, service] = v.split(":");
            return { folder_id: parseInt(folder_id), service };
        });

        actions.setCustomTranscriberPaymentFoldersServices(transcriber_payment, folders_services);
    }

    if (!unpaid_folders.length) return null;

    return (
        <Combobox
            value={value}
            onValueChange={onValueChange}
            multiple
            autoHighlight
        >
            <ComboboxAnchor className="h-full min-h-10 flex-wrap px-3 py-2">
                <ComboboxTrigger className="w-full">
                    <div className="w-full text-left text-sm">Ajouter/Supprimer un dossier à la facture</div>
                    <ChevronDown className="h-4 w-4 absolute top-3 right-2" />
                </ComboboxTrigger>
            </ComboboxAnchor>
            <ComboboxContent>
                <ComboboxEmpty>No tricks found.</ComboboxEmpty>
                {unpaid_folders.map((f) => (
                    <ComboboxItem key={`${f.id}:${f.service}`} value={`${f.id}:${f.service}`}>
                        <p className="h-5 text-sm items-center flex gap-x-4" >
                            <b>#{f.pydio_id}</b>
                            <span>Dr. {f.doctor?.profile.last_name}</span>
                            <span>{SERVICE_LABEL[f.service]}</span>
                            <span>{displayDuration(f.duration)}</span>
                        </p>
                    </ComboboxItem>
                ))}
            </ComboboxContent>
        </Combobox>
    );
}

function PaymentInvoiceDropBox({ transcriber_payment }: { transcriber_payment: TranscriberPayment }) {
    const { toast } = useToast();
    const { actions, state: { uploadTranscriberPaymentInvoice: { loading, data, error } } } = useTranscribersPayments();

    const onDrop = (files: File[]) => files.length > 0
        && actions.uploadTranscriberPaymentInvoice(transcriber_payment, files[0]);

    const has_invoice = Boolean(transcriber_payment.invoice.path);
    const value = has_invoice ? [new File(["foo"], "foo.txt", { type: "text/plain" })] : [];

    useEffect(() => {
        if (data) {
            toast({
                title: "Facture envoyée",
                description: "Votre facture a été téléchargé avec succès.",
                variant: "success",
                duration: 2000
            });
        }

        if (error) {
            toast({
                title: "Facture non envoyée",
                description: "Une erreur est survenue lors de l'envoi de la facture.",
                variant: "destructive",
                duration: 2000
            });
        }
    }, [data, error]);

    return (
        <FileUpload.Root disabled={loading} value={value} onValueChange={onDrop} className="mt-6">
            <FileUpload.Dropzone
                className={cn("cursor-pointer", has_invoice && 'bg-green-300/30 hover:bg-green-300/20')}>
                <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center justify-center rounded-full border border-white/20 p-2.5">
                        {has_invoice ? <Check className="size-6 text-muted-foreground" /> : <Upload className="size-6 text-muted-foreground" />}
                    </div>
                    <p className="font-medium text-sm">
                        {has_invoice ? "Facture déposé" : "Déposez votre facture ici"}
                    </p>
                    <p className="text-muted-foreground text-xs">
                        {has_invoice ? "Cliquez pour changer la facture" : "Ou cliquez pour sélectionner un fichier"}
                    </p>
                </div>
            </FileUpload.Dropzone>
            <FileUpload.Trigger />
            <FileUpload.List>
                <FileUpload.Item>
                    <FileUpload.ItemPreview />
                    <FileUpload.ItemMetadata />
                    <FileUpload.ItemProgress />
                    <FileUpload.ItemDelete />
                </FileUpload.Item>
            </FileUpload.List>
            <FileUpload.Clear />
        </FileUpload.Root>
    );
}